import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { Cell } from "../grid/Cell";
import type { GameLocale } from "../../i18n/types";
import { getMessages } from "../../i18n/messages";

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  locale: GameLocale;
};

export const InfoModal = ({ isOpen, handleClose, locale }: Props) => {
  const m = getMessages(locale);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={handleClose}
      >
        <div className="flex items-center justify-center min-h-screen p-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="relative w-full max-w-sm bg-white dark:bg-gray-800 rounded-lg px-5 py-5 text-left shadow-xl">
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XIcon className="h-5 w-5" />
              </button>

              <Dialog.Title
                as="h3"
                className="text-lg font-bold text-gray-900 dark:text-white text-center mb-3"
              >
                {m.infoTitle}
              </Dialog.Title>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {m.infoIntro}
              </p>

              <div className="space-y-3 mb-4">
                <div>
                  <div
                    className="grid-row"
                    style={{ maxWidth: "220px", height: "40px", marginBottom: "4px" }}
                  >
                    <Cell value="D" status="correct" />
                    <Cell value="A" />
                    <Cell value="N" />
                    <Cell value="S" />
                    <Cell value="K" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <strong>D</strong> {m.infoEx1}
                  </p>
                </div>

                <div>
                  <div
                    className="grid-row"
                    style={{ maxWidth: "220px", height: "40px", marginBottom: "4px" }}
                  >
                    <Cell value="S" />
                    <Cell value="P" />
                    <Cell value="I" status="present" />
                    <Cell value="L" />
                    <Cell value="S" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <strong>I</strong> {m.infoEx2}
                  </p>
                </div>

                <div>
                  <div
                    className="grid-row"
                    style={{ maxWidth: "220px", height: "40px", marginBottom: "4px" }}
                  >
                    <Cell value="H" />
                    <Cell value="U" />
                    <Cell value="N" />
                    <Cell value="D" status="absent" />
                    <Cell value="E" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <strong>D</strong> {m.infoEx3}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                  {m.infoCredit}{" "}
                  <a
                    href="https://www.nytimes.com/games/wordle"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    Wordle
                  </a>{" "}
                  {locale === "no" ? "av" : "af"} Josh Wardle.
                </p>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
